import React, { useState, useCallback } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input, TextArea, Checkbox } from '@progress/kendo-react-inputs';
import { DropDownList, MultiSelect, ComboBox, type MultiSelectChangeEvent } from '@progress/kendo-react-dropdowns';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { Button } from '@progress/kendo-react-buttons';
import { useSchedule } from '../../context/ScheduleContext';
import type { KendoItem } from '../../utils/kendoAdapter';
import type { CustomAttributeValue, CustomAttributeDefinition, Client } from '../../types';

// ─── Props ─────────────────────────────────────────────────────────────────

export interface CustomEditFormProps {
  dataItem: KendoItem;
  isNew?: boolean;
  onClose: () => void;
  /** Submit receives the updated KendoItem */
  onSubmit: (item: KendoItem) => void;
}

// ─── Custom Attribute Fields ───────────────────────────────────────────────

function AttrField({
  def,
  value,
  onChange,
}: {
  def: CustomAttributeDefinition;
  value: CustomAttributeValue['value'];
  onChange: (v: CustomAttributeValue['value']) => void;
}) {
  if (def.type === 'text') {
    return (
      <Input
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.value)}
        style={{ width: '100%' }}
      />
    );
  }
  if (def.type === 'number') {
    return (
      <Input
        type="number"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.value === '' ? null : Number(e.value))}
        style={{ width: '100%' }}
      />
    );
  }
  if (def.type === 'date') {
    return (
      <Input
        type="date"
        value={(value as string)?.split('T')[0] ?? ''}
        onChange={(e) => onChange(e.value)}
        style={{ width: '100%' }}
      />
    );
  }
  if (def.type === 'time') {
    return (
      <Input
        type="time"
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.value)}
        style={{ width: '100%' }}
      />
    );
  }
  if (def.type === 'select') {
    const items = [{ label: '— None —', value: '' }, ...(def.options ?? [])];
    return (
      <DropDownList
        data={items}
        textField="label"
        dataItemKey="value"
        value={items.find((o) => o.value === (value ?? '')) ?? items[0]}
        onChange={(e) => onChange(e.value.value || null)}
        style={{ width: '100%' }}
      />
    );
  }
  if (def.type === 'multiselect') {
    const selected = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {def.options?.map((opt) => (
          <Checkbox
            key={opt.value}
            label={opt.label}
            value={selected.includes(opt.value)}
            onChange={(e) => {
              const next = e.value
                ? [...selected, opt.value]
                : selected.filter((v) => v !== opt.value);
              onChange(next.length > 0 ? next : null);
            }}
          />
        ))}
      </div>
    );
  }
  return null;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const HTML_TAG_RE = /<[a-z][\s\S]*>/i;

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

// ─── Main form ─────────────────────────────────────────────────────────────

export function CustomEditForm({ dataItem, isNew = false, onClose, onSubmit }: CustomEditFormProps) {
  const { config, handleDeleteEvent } = useSchedule();
  const { timezone, resources = [], customAttributeDefinitions = [], clients = [], locations = [] } = config;

  const [title, setTitle] = useState(dataItem.title ?? '');
  const [start, setStart] = useState<Date>(dataItem.start);
  const [end, setEnd] = useState<Date>(dataItem.end);
  const [isAllDay, setIsAllDay] = useState(dataItem.isAllDay);
  const [recurrenceRule, setRecurrenceRule] = useState<string>(
    dataItem.recurrenceRule ?? '',
  );
  type ResourceItem = { text: string; value: string };
  const resourceItems: ResourceItem[] = resources.map((r) => ({ text: r.name, value: r.id }));
  const [resourceIds, setResourceIds] = useState<string[]>(
    dataItem.resourceIds?.length ? dataItem.resourceIds : (dataItem.resourceId ? [dataItem.resourceId] : []),
  );
  const selectedResourceItems = resourceItems.filter((r) => resourceIds.includes(r.value));
  const [color, setColor] = useState(dataItem.color ?? '#3B82F6');
  const [description, setDescription] = useState(dataItem.description ?? '');
  const [customAttrs, setCustomAttrs] = useState<CustomAttributeValue[]>(
    dataItem.customAttributes ?? [],
  );
  const [clientId, setClientId] = useState<string>(dataItem.clientId ?? '');
  const [isGroupTherapy, setIsGroupTherapy] = useState(dataItem.isGroupTherapy ?? false);
  const [groupCapacity, setGroupCapacity] = useState<number | ''>(dataItem.groupCapacity ?? '');
  type ClientItem = { text: string; value: string };
  const clientItems: ClientItem[] = clients.map((c: Client) => ({ text: c.name, value: c.id }));
  const [groupClientIds, setGroupClientIds] = useState<string[]>(dataItem.groupClientIds ?? []);
  const selectedClientItems = clientItems.filter((c) => groupClientIds.includes(c.value));
  const [groupType, setGroupType] = useState<string>(dataItem.groupType as string ?? '');
  const [facilityType, setFacilityType] = useState<string>(dataItem.facilityType as string ?? '');
  const [location, setLocation] = useState<string>(dataItem.location as string ?? '');

  const getAttrValue = (key: string) =>
    customAttrs.find((a) => a.key === key)?.value ?? null;

  const setAttrValue = (key: string, value: CustomAttributeValue['value']) => {
    setCustomAttrs((prev) => {
      const rest = prev.filter((a) => a.key !== key);
      return value !== null && value !== '' ? [...rest, { key, value }] : rest;
    });
  };

  const handleSave = useCallback(() => {
    if (!stripHtml(title)) return;
    onSubmit({
      ...dataItem,
      title,
      start,
      end,
      isAllDay,
      recurrenceRule: recurrenceRule || null,
      resourceId: resourceIds[0] || undefined,
      resourceIds: resourceIds.length > 0 ? resourceIds : undefined,
      color,
      description,
      customAttributes: customAttrs,
      clientId: clientId || undefined,
      isGroupTherapy: isGroupTherapy || undefined,
      groupCapacity: isGroupTherapy && groupCapacity !== '' ? Number(groupCapacity) : undefined,
      groupClientIds: isGroupTherapy && groupClientIds.length > 0 ? groupClientIds : undefined,
      groupType: isGroupTherapy && groupType ? groupType : undefined,
      facilityType: isGroupTherapy && facilityType ? facilityType : undefined,
      location: location || undefined,
    });
  }, [title, start, end, isAllDay, recurrenceRule, resourceIds, color, description, customAttrs, clientId, isGroupTherapy, groupCapacity, groupClientIds, groupType, facilityType, location, dataItem, onSubmit]);

  const handleDelete = useCallback(() => {
    if (!window.confirm('Delete this event?')) return;
    handleDeleteEvent(String(dataItem.id));
    onClose();
  }, [dataItem.id, handleDeleteEvent, onClose]);

  const canDelete = dataItem.deletable && !isNew;
  const isMobile = window.innerWidth < 768;

  // Suppress TypeScript tz warning — kept for future use
  void timezone;

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 12,
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#475569',
  };
  const dividerStyle: React.CSSProperties = {
    borderTop: '1px solid #e2e8f0',
    paddingTop: 8,
    marginTop: 4,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#94a3b8',
    marginBottom: 8,
  };

  return (
    <Dialog
      title={isNew ? 'New Event' : 'Edit Event'}
      onClose={onClose}
      width={isMobile ? Math.min(window.innerWidth - 48, 420) : 560}
    >
      <div style={{ padding: '8px 4px', maxHeight: isMobile ? '72vh' : '70vh', overflowY: 'auto' }}>
        {/* Title */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Title *</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.value)}
            placeholder="Event title — HTML supported (e.g. <b>Bold</b>, <i>Italic</i>)"
            autoFocus
          />
          {HTML_TAG_RE.test(title) && (
            <div style={{
              padding: '4px 8px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 4,
              fontSize: 12,
              color: '#1e293b',
            }}>
              <span style={{ fontSize: 10, color: '#94a3b8', display: 'block', marginBottom: 2 }}>Preview</span>
              {/* eslint-disable-next-line react/no-danger */}
              <span dangerouslySetInnerHTML={{ __html: title }} />
            </div>
          )}
        </div>

        {/* All day */}
        <div style={{ ...fieldStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Checkbox
            value={isAllDay}
            onChange={(e) => setIsAllDay(e.value)}
            label="All day"
          />
        </div>

        {/* Start / End */}
        {!isAllDay && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Start</label>
              <DateTimePicker
                value={start}
                onChange={(e) => e.value && setStart(e.value)}
                format="MM/dd/yyyy h:mm a"
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>End</label>
              <DateTimePicker
                value={end}
                onChange={(e) => e.value && setEnd(e.value)}
                format="MM/dd/yyyy h:mm a"
                min={start}
              />
            </div>
          </div>
        )}

        {/* Resources */}
        {resources.length > 0 && (
          <div style={fieldStyle}>
            <label style={labelStyle}>Resources</label>
            <MultiSelect
              data={resourceItems}
              textField="text"
              dataItemKey="value"
              value={selectedResourceItems}
              onChange={(e: MultiSelectChangeEvent) =>
                setResourceIds((e.value as ResourceItem[]).map((r) => r.value))
              }
              filterable
              placeholder="Search resources…"
              style={{ width: '100%' }}
            />
          </div>
        )}

        {/* Client */}
        {clients.length > 0 && !isGroupTherapy && (
          <div style={fieldStyle}>
            <label style={labelStyle}>Client</label>
            <ComboBox
              data={clients}
              textField="name"
              dataItemKey="id"
              value={clients.find((c: Client) => c.id === clientId) ?? null}
              onChange={(e) => setClientId((e.value as Client | null)?.id ?? '')}
              filterable
              onFilterChange={(e) => e}
              placeholder="Search client…"
              style={{ width: '100%' }}
              clearButton
            />
          </div>
        )}

        {/* Color */}
        <div style={{ ...fieldStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Color</label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{ width: 36, height: 28, border: '1px solid #cbd5e1', borderRadius: 4, padding: 2, cursor: 'pointer' }}
          />
          <span style={{ fontSize: 12, color: '#64748b' }}>{color}</span>
        </div>

        {/* Location / Room */}
        {locations.length > 0 && (
          <div style={fieldStyle}>
            <label style={labelStyle}>Location / Room</label>
            <DropDownList
              data={[{ label: '— None —', value: '' }, ...locations.map((l) => ({ label: l, value: l }))]}
              textField="label"
              dataItemKey="value"
              value={{ label: location || '— None —', value: location }}
              onChange={(e) => setLocation(e.value.value ?? '')}
              style={{ width: '100%' }}
            />
          </div>
        )}

        {/* Description */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Description</label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.value)}
            rows={2}
            style={{ resize: 'vertical' }}
          />
        </div>

        {/* Recurrence — RRULE string input */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Recurrence (RRULE)</label>
          <Input
            value={recurrenceRule}
            onChange={(e) => setRecurrenceRule(e.value)}
            placeholder="e.g. FREQ=WEEKLY;BYDAY=MO,WE,FR"
          />
          <span style={{ fontSize: 10, color: '#94a3b8' }}>
            iCalendar RRULE format. Leave blank for one-time events.
          </span>
        </div>

        {/* Group Therapy */}
        <>
          <div style={dividerStyle}>Group Therapy</div>
          <div style={{ ...fieldStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Checkbox
              value={isGroupTherapy}
              onChange={(e) => setIsGroupTherapy(e.value)}
              label="Flag as Group Therapy Session"
            />
          </div>
          {isGroupTherapy && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Group Type</label>
                  <DropDownList
                    data={['DBT', 'CBT', 'trauma', 'substance_use', 'anxiety', 'depression', 'PTSD', 'family', 'grief', 'psychoeducation', 'mindfulness', 'anger_management']}
                    value={groupType || null}
                    onChange={(e) => setGroupType(e.value ?? '')}
                    defaultItem="Select type…"
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Modality</label>
                  <DropDownList
                    data={[
                      { text: 'In-person', value: 'in_person' },
                      { text: 'Telehealth', value: 'telehealth' },
                      { text: 'Hybrid', value: 'hybrid' },
                    ]}
                    textField="text"
                    dataItemKey="value"
                    value={[
                      { text: 'In-person', value: 'in_person' },
                      { text: 'Telehealth', value: 'telehealth' },
                      { text: 'Hybrid', value: 'hybrid' },
                    ].find((o) => o.value === facilityType) ?? null}
                    onChange={(e) => setFacilityType(e.value?.value ?? '')}
                    defaultItem={{ text: 'Select modality…', value: '' }}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Max Clients / Capacity</label>
                <Input
                  type="number"
                  value={String(groupCapacity)}
                  onChange={(e) => setGroupCapacity(e.value === '' ? '' : Math.max(1, Number(e.value)))}
                  placeholder="e.g. 8"
                  style={{ width: '100%' }}
                />
              </div>
              {clients.length > 0 && (
                <div style={fieldStyle}>
                  <label style={labelStyle}>
                    Assigned Clients
                    {groupCapacity !== '' && (
                      <span style={{ fontWeight: 400, color: groupClientIds.length > Number(groupCapacity) ? '#ef4444' : '#64748b', marginLeft: 6 }}>
                        ({groupClientIds.length} / {groupCapacity})
                      </span>
                    )}
                  </label>
                  <MultiSelect
                    data={clientItems}
                    textField="text"
                    dataItemKey="value"
                    value={selectedClientItems}
                    onChange={(e: MultiSelectChangeEvent) =>
                      setGroupClientIds((e.value as ClientItem[]).map((c) => c.value))
                    }
                    filterable
                    placeholder="Search clients…"
                    style={{ width: '100%' }}
                  />
                  {groupCapacity !== '' && groupClientIds.length > Number(groupCapacity) && (
                    <span style={{ fontSize: 11, color: '#ef4444' }}>
                      Exceeds capacity — reduce clients or increase max.
                    </span>
                  )}
                </div>
              )}
              {clients.length === 0 && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12 }}>
                  No clients configured. Add <code>clients</code> to the schedule config to enable client assignment.
                </div>
              )}
            </>
          )}
        </>

        {/* Custom attributes */}
        {customAttributeDefinitions.length > 0 && (
          <>
            <div style={dividerStyle}>Custom Fields</div>
            {customAttributeDefinitions.map((def) => (
              <div key={def.key} style={fieldStyle}>
                <label style={labelStyle}>
                  {def.label}
                  {def.required && <span style={{ color: '#ef4444' }}> *</span>}
                </label>
                <AttrField
                  def={def}
                  value={getAttrValue(def.key)}
                  onChange={(v) => setAttrValue(def.key, v)}
                />
              </div>
            ))}
          </>
        )}
      </div>

      <DialogActionsBar>
        {canDelete && (
          <Button themeColor="error" fillMode="outline" onClick={handleDelete}>
            Delete
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button themeColor="primary" onClick={handleSave} disabled={!stripHtml(title)}>
          {isNew ? 'Create' : 'Save'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
}
